"use client";

import "swiper/css";

import { useRef, useState } from "react";

import cn from "classnames";
import type { RefObject } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { CarouselCenteredNavButton } from "@/components/carouselCentered/carouselCenteredNavButton.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import type { FeatureCardsSection as FeatureCardsSectionType } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

import {
  FeatureCardsSectionCard,
  type FeatureCardsSectionCardData,
} from "./featureCardsSectionCard.component";

const DESKTOP_PAGE_SIZE = 3;

function mapCards(
  cards: FeatureCardsSectionType["cards"],
): FeatureCardsSectionCardData[] {
  if (!cards?.length) return [];

  return cards.flatMap((card) => {
    if (!card?._key || !card.title?.trim() || !card.description?.trim()) {
      return [];
    }

    const imageSrc = card.image?.asset
      ? urlFor(card.image).width(800).height(500).url()
      : undefined;
    const mobileImageSrc = card.mobileImage?.asset
      ? urlFor(card.mobileImage).width(800).height(500).url()
      : undefined;

    return [
      {
        id: card._key,
        title: card.title,
        description: card.description,
        footnote: card.footnote?.trim() || undefined,
        imageSrc,
        imageAlt: card.image?.alt?.trim() || card.title,
        mobileImageSrc,
        mobileImageAlt:
          card.mobileImage?.alt?.trim() ||
          card.image?.alt?.trim() ||
          card.title,
      },
    ];
  });
}

function PaginationDots({
  count,
  activeIndex,
  swiperRef,
  labelPrefix,
  ariaLabel,
  slidesPerGroup = 1,
}: {
  count: number;
  activeIndex: number;
  swiperRef: RefObject<SwiperType | null>;
  labelPrefix: "slide" | "page";
  ariaLabel: string;
  slidesPerGroup?: number;
}) {
  return (
    <div
      data-testid={
        labelPrefix === "page"
          ? "feature-cards-desktop-pagination"
          : "feature-cards-pagination"
      }
      className="flex items-center justify-center gap-2"
      role="tablist"
      aria-label={ariaLabel}>
      {Array.from({ length: count }, (_, index) => {
        const isActive = index === activeIndex;
        return (
          <button
            key={`${labelPrefix}-${index}`}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={`Go to ${labelPrefix} ${index + 1}`}
            className={cn(
              "size-2 rounded-full transition-colors",
              isActive ? "bg-secondary" : "bg-greyscale-strength/40",
            )}
            onClick={() => swiperRef.current?.slideTo(index * slidesPerGroup)}
          />
        );
      })}
    </div>
  );
}

function CarouselControls({
  swiperRef,
  pageCount,
  activeIndex,
  labelPrefix,
  ariaLabel,
  testId,
  slidesPerGroup = 1,
}: {
  swiperRef: RefObject<SwiperType | null>;
  pageCount: number;
  activeIndex: number;
  labelPrefix: "slide" | "page";
  ariaLabel: string;
  testId: string;
  slidesPerGroup?: number;
}) {
  if (pageCount <= 1) return null;

  return (
    <div
      data-testid={testId}
      className="mt-6 flex w-full items-center justify-between gap-4 px-1">
      <CarouselCenteredNavButton swiperRef={swiperRef} direction="prev" />
      <PaginationDots
        count={pageCount}
        activeIndex={activeIndex}
        swiperRef={swiperRef}
        labelPrefix={labelPrefix}
        ariaLabel={ariaLabel}
        slidesPerGroup={slidesPerGroup}
      />
      <CarouselCenteredNavButton swiperRef={swiperRef} direction="next" />
    </div>
  );
}

export function FeatureCardsSection({
  block,
  className,
}: FeatureCardsSectionProps) {
  const mobileSwiperRef = useRef<SwiperType | null>(null);
  const desktopSwiperRef = useRef<SwiperType | null>(null);
  const [mobileActiveIndex, setMobileActiveIndex] = useState(0);
  const [desktopActiveIndex, setDesktopActiveIndex] = useState(0);

  const heading = block.heading?.trim();
  const cards = mapCards(block.cards);
  const theme = block.theme ?? "light";
  const needsDesktopPagination = cards.length > DESKTOP_PAGE_SIZE;
  const desktopPageCount = Math.ceil(cards.length / DESKTOP_PAGE_SIZE);

  if (!heading || !cards.length) return null;

  return (
    <section
      data-testid="feature-cards-section"
      className={cn("w-full bg-white py-10 md:py-14 lg:py-16", className)}
      aria-labelledby="feature-cards-heading">
      <div className="container mx-auto flex flex-col gap-8 px-5 sm:px-6 md:gap-10 md:px-8 lg:gap-12">
        <header className="mx-auto flex flex-col items-center gap-4 text-center">
          <Text
            id="feature-cards-heading"
            variant="h3"
            component="h3"
            className="text-greyscale-headline">
            {heading}
          </Text>
          {block.subtitle?.trim() ? (
            <Text component="p" className="text-greyscale-body">
              {block.subtitle}
            </Text>
          ) : null}
        </header>

        {!needsDesktopPagination ? (
          <div
            data-testid="feature-cards-grid"
            className="hidden gap-6 md:grid md:grid-cols-3 md:gap-8">
            {cards.map((card) => (
              <FeatureCardsSectionCard
                key={card.id}
                card={card}
                theme={theme}
              />
            ))}
          </div>
        ) : (
          <div
            data-testid="feature-cards-desktop-carousel"
            className="hidden md:block">
            <Swiper
              data-testid="feature-cards-desktop-swiper"
              slidesPerView={DESKTOP_PAGE_SIZE}
              slidesPerGroup={DESKTOP_PAGE_SIZE}
              spaceBetween={32}
              allowTouchMove
              onSwiper={(swiper) => {
                desktopSwiperRef.current = swiper;
                setDesktopActiveIndex(swiper.snapIndex);
              }}
              onSlideChange={(swiper) =>
                setDesktopActiveIndex(swiper.snapIndex)
              }
              className="w-full! [&_.swiper-slide]:h-auto!">
              {cards.map((card) => (
                <SwiperSlide key={card.id} className="h-auto">
                  <FeatureCardsSectionCard card={card} theme={theme} />
                </SwiperSlide>
              ))}
            </Swiper>
            <CarouselControls
              swiperRef={desktopSwiperRef}
              pageCount={desktopPageCount}
              activeIndex={desktopActiveIndex}
              labelPrefix="page"
              ariaLabel="Feature card pages"
              testId="feature-cards-desktop-controls"
              slidesPerGroup={DESKTOP_PAGE_SIZE}
            />
          </div>
        )}

        <div data-testid="feature-cards-carousel" className="md:hidden">
          <Swiper
            data-testid="feature-cards-swiper"
            slidesPerView={1}
            spaceBetween={16}
            allowTouchMove
            onSwiper={(swiper) => {
              mobileSwiperRef.current = swiper;
              setMobileActiveIndex(swiper.realIndex);
            }}
            onSlideChange={(swiper) => setMobileActiveIndex(swiper.realIndex)}
            className="w-full! [&_.swiper-slide]:h-auto!">
            {cards.map((card) => (
              <SwiperSlide key={card.id} className="h-auto">
                <FeatureCardsSectionCard card={card} theme={theme} />
              </SwiperSlide>
            ))}
          </Swiper>

          <CarouselControls
            swiperRef={mobileSwiperRef}
            pageCount={cards.length}
            activeIndex={mobileActiveIndex}
            labelPrefix="slide"
            ariaLabel="Feature card slides"
            testId="feature-cards-controls"
          />
        </div>

        {block.footer ? (
          <div
            data-testid="feature-cards-footer"
            className="text-greyscale-body mx-auto max-w-4xl text-center">
            <RichText>{block.footer}</RichText>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export type FeatureCardsSectionProps = {
  block: FeatureCardsSectionType;
  className?: string;
};
